import { memo, useCallback, useEffect, useRef, useState } from 'react'

import { Request } from 'commons/utils/request'
import debounce from 'lodash/debounce'
import isEqual from 'lodash/isEqual'
import { IconName, IconSize } from 'ui/components/Icon'
import { Popper } from 'ui/components/Popper'

import { Item } from './Item'
import { MultiSelectAutoSuggestProps } from './MultiSelectAutoSuggestProps'
import { OptionItem } from './OptionItem'
import * as SC from './style'

export const MultiSelectAutoSuggest = memo((props: MultiSelectAutoSuggestProps) => {
  const {
    value,
    onChange,
    options,
    valueTitle,
    equalCondition,
    serverOptions,
    'data-qa': propsDataQA,
    'data-qa-input': propsDataQAInput,
    ...rest
  } = props

  const dataQA = propsDataQA || 'multi-select-auto-suggest'
  const dataQAInput = propsDataQAInput || 'multi-select-auto-suggest-input'

  const listRef = useRef([])
  const inputRef = useRef<HTMLInputElement>()
  const [edit, setEdit] = useState(false)
  const [open, setOpen] = useState(false)
  const [text, setText] = useState('')
  const [serverItems, setServerItems] = useState<Item[]>([])
  const [rightColumnItems, setRightColumnItems] = useState<Item[]>([])
  const ref = useRef<any>()
  const dropDownRef = useRef<HTMLDivElement>(null)
  const items = options && options.length > 0 ? options : serverItems

  const onSearch = debounce(
    useCallback((term: string) => {
      setText(term)
    }, []),
    500
  )

  const onOptionChange = useCallback(
    (optionValue: string, disabled?: boolean) => {
      if (ref.current) {
        ref.current.focus()
      }

      if (disabled) {
        return
      }

      const cond = (el: string | any) => (equalCondition && equalCondition(el, optionValue)) || isEqual(el, optionValue)
      const index = (value || []).findIndex(cond)

      if (index === -1) {
        if (onChange) {
          onChange((value || []).concat(optionValue))
        }
        const selectedItem = items.filter((item) => item.value === optionValue)
        setRightColumnItems((prev) => prev.concat(selectedItem[0]))
      } else {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const copy = [...value]
        copy.splice(index, 1)
        setRightColumnItems((prev) => prev.filter((item) => item.value !== optionValue))
        if (onChange) {
          onChange(copy)
        }
      }
    },
    [value, equalCondition, onChange, items]
  )

  const startEdit = useCallback(() => {
    setEdit(true)
    setOpen(true)
  }, [])

  useEffect(() => {
    if (edit) {
      // temp solution, for setting focus after floating ui manage their focus
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current?.focus()
        }
      }, 0)
    }
  }, [edit])

  const endEdit = useCallback(() => {
    setEdit(false)
    setOpen(false)
    setText('')
    onSearch('')
  }, [onSearch])

  const onTextChange = useCallback(
    (text: string) => {
      setText(text)
      onSearch(text)
    },
    [onSearch]
  )

  const stopClickPropagation = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const typeOptions = items.filter(
    (item) =>
      !(value || []).some((i: any) =>
        !!i && !!item.value ? i === item.value : !!i?.id && !!item?.value?.id ? i.id === item.value.id : false
      )
  )

  const typeSelectedOptions = items.filter((item) =>
    (value || []).some((i: any) =>
      !!i && !!item.value ? i === item.value : !!i?.id && !!item?.value?.id ? i.id === item.value.id : false
    )
  )

  const optionsToSelect = typeOptions.map((item) => (
    <OptionItem
      key={item.value?.id || item.value}
      text={item.label}
      value={item.value}
      disabled={item.disabled}
      title={item.title}
      checked={false}
      onClick={onOptionChange}
    />
  ))

  const selectedOptions = (serverOptions ? rightColumnItems : typeSelectedOptions).map((item) => (
    <OptionItem
      key={item.value?.id || item.value}
      text={item.label}
      value={item.value}
      disabled={item.disabled}
      title={item.title}
      checked
      onClick={onOptionChange}
    />
  ))

  const dropDownRenderer = useCallback(
    () => (
      <SC.Columns onMouseDownCapture={stopClickPropagation}>
        <SC.LeftColumn data-qa={`${dataQA}-options-to-select`}>
          {optionsToSelect.length ? optionsToSelect : <SC.Empty>No items to select.</SC.Empty>}
        </SC.LeftColumn>
        <SC.RightColumn data-qa={`${dataQA}-selected-options`}>
          {selectedOptions.length ? selectedOptions : <SC.Empty>No selected items.</SC.Empty>}
        </SC.RightColumn>
      </SC.Columns>
    ),
    [dataQA, optionsToSelect, selectedOptions, stopClickPropagation]
  )

  const labels = value?.map((val) => {
    let item = items.filter((item) => item.value === val)
    if (!item || item.length == 0) {
      item = rightColumnItems.filter((item) => item.value === val)
    }
    return item && item.length ? item[0].label : val
  })

  const fetchOptions = useCallback(
    async (customFilters?: any) => {
      if (serverOptions) {
        const data = await Request.post(serverOptions.url, {
          _options: {
            offset: 0,
            limit: serverOptions.limit,
            filters: customFilters
              ? customFilters
              : text && serverOptions.searchFields?.length
                ? ([
                    {
                      type: 'or',
                      filters: serverOptions.searchFields.map((field) => ({
                        field,
                        type: 'like',
                        value: `%${text}%`
                      }))
                    }
                  ]
                    .concat(serverOptions.filters as any)
                    .filter(Boolean) as any)
                : serverOptions.filters
          }
        })

        // TODO: refactor it. We have to do this tricky fix, because different API endpoint returns
        // TODO: data in different formats.
        // eslint-disable-next-line
        const collection = Array.isArray(data?.records) ? data.records : Array.isArray(data) ? data : []
        const processedCollection = collection.map(serverOptions.mapper).filter(Boolean)
        if (customFilters) {
          setRightColumnItems(processedCollection)
        } else {
          setServerItems(processedCollection)
        }
      }
    },
    [serverOptions, text]
  )

  useEffect(() => {
    if (serverOptions) {
      fetchOptions()
    }
  }, [
    serverOptions && serverOptions.filters ? serverOptions.filters.map((f) => JSON.stringify(f)).join() : undefined,
    text
  ])

  useEffect(() => {
    if (value && value.length > 0) {
      fetchOptions([
        {
          type: 'in',
          field: 'id',
          value: value
        }
      ])
    }
  }, [])

  return (
    <Popper
      scrollClose={true}
      autoSize={true}
      autoSizeWidth={'fill'}
      listRef={listRef}
      placement="bottom-start"
      opened={open}
      onOpen={startEdit}
      onClose={endEdit}
      render={() => (
        <SC.DropDown open={open} ref={dropDownRef}>
          {dropDownRenderer()}
        </SC.DropDown>
      )}
    >
      <div style={{ height: '40px' }} data-qa={dataQA}>
        <SC.ValueSC {...rest} role="listbox">
          {edit ? (
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            <SC.WrappedInput value={text} onChange={onTextChange} ref={inputRef} data-qa={dataQAInput} />
          ) : (
            <SC.Title>{labels?.length ? labels.join(',') : valueTitle}</SC.Title>
          )}
          {!!value?.length && <SC.Count>{value.length}</SC.Count>}
          <SC.WrappedIcon name={IconName.DOWN} size={IconSize.XS} open={open} />
        </SC.ValueSC>
      </div>
    </Popper>
  )
})
